// ─── batch-71 profiel-in-beeld (FO-538) — editor, documentversies, terugzetten ─
//
// Gedeelde schema's en DTO's tussen de API en de editor op
// `/medewerkers/[id]/profiel/in-beeld`. Eén bron: het formulier kan niets sturen
// dat de API weigert.
//
// Twee opslagniveaus (spec §3.B5):
//   - "Op de medewerker": `PATCH /employees/:id/profiel` + werkervaring-CRUD;
//   - "Alleen voor dit document": `ProfielDocumentVersie.overridesJson`, dit schema.
//
// Opleidingsvinkjes staan hier bewust NIET in: die zijn afgeleid van de certificaten
// en niet met de hand aan te vinken (§B5). `.strict()` laat een poging daartoe falen.
import { z } from 'zod'
import { PROFIEL_FUNCTIES, type ProfielData, type ProfielFunctie } from './profiel'
import type { ProfielLayout } from './profiel-layout'

// ─── Fotobijsnijding ─────────────────────────────────────────────────────────

/** Welk deel van de foto in het kader valt. `x`/`y` 0-1 (0,5 = midden), `zoom` 1-4. */
export const profielFotoUitsnedeSchema = z
  .object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    zoom: z.number().min(1).max(4),
  })
  .strict()
export type ProfielFotoUitsnede = z.infer<typeof profielFotoUitsnedeSchema>

/** Gezichten zitten in de bovenste helft: standaard niet precies het midden. */
export const PROFIEL_FOTO_UITSNEDE_STANDAARD: ProfielFotoUitsnede = { x: 0.5, y: 0.35, zoom: 1 }

// ─── Beschikbaarheid ─────────────────────────────────────────────────────────

export const PROFIEL_BESCHIKBAARHEID_KEYS = ['dagdienst', 'ploegendienst', 'weekend', 'inval'] as const
export type ProfielBeschikbaarheidKey = (typeof PROFIEL_BESCHIKBAARHEID_KEYS)[number]

export const PROFIEL_BESCHIKBAARHEID_LABELS: Record<ProfielBeschikbaarheidKey, string> = {
  dagdienst: 'Dagdienst',
  ploegendienst: 'Ploegendienst',
  weekend: 'Weekend',
  inval: 'Inval',
}

/** `Employee.profielBeschikbaarheidOverride`: alleen de aangevinkte stand per vakje. */
export const profielBeschikbaarheidOverrideSchema = z
  .object({
    dagdienst: z.boolean(),
    ploegendienst: z.boolean(),
    weekend: z.boolean(),
    inval: z.boolean(),
  })
  .strict()
export type ProfielBeschikbaarheidOverride = z.infer<typeof profielBeschikbaarheidOverrideSchema>

// ─── PATCH /employees/:id/profiel ────────────────────────────────────────────

// Lege strings vallen af, max 20 competenties (gelijk aan het bestaande contract).
export const updateProfielSchema = z.object({
  profielTekst: z.string().nullable().optional(),
  profielFuncties: z.array(z.enum(PROFIEL_FUNCTIES)).optional(),
  kerncompetenties: z.array(z.string().trim().min(1)).max(20).optional(),
  // FO-304-E — klant-introductietekst (klantwaardig; getoond op de klant-zijde
  // monteurs-pagina). Los van de interne `profielTekst`.
  klantProfielTekst: z.string().max(2000).nullable().optional(),
  // batch-71 — handmatige beschikbaarheid; null = weer afleiden uit de roostervoorkeur.
  profielBeschikbaarheidOverride: profielBeschikbaarheidOverrideSchema.nullable().optional(),
  // batch-71 — uitsnede van de foto; null = standaard (midden).
  profielFotoUitsnede: profielFotoUitsnedeSchema.nullable().optional(),
})
export type UpdateProfielInput = z.infer<typeof updateProfielSchema>

// ─── Document-overrides ("alleen voor dit document") ─────────────────────────

// Dezelfde grenzen en meldingen als het dossier (`werkervaringBlokSchema`): een blok dat op
// de medewerker mag, mag ook als afwijking in een document (review K-a).
const overrideWerkervaringSchema = z
  .object({
    functie: z
      .string()
      .trim()
      .min(1, 'Elk blok heeft een functie nodig.')
      .max(500, 'De functie is te lang (maximaal 500 tekens).'),
    bedrijf: z.string().trim().max(500, 'De naam van het bedrijf is te lang (maximaal 500 tekens).').nullable(),
    periode: z.string().trim().max(210, 'De periode is te lang (maximaal 210 tekens).').nullable(),
    bullets: z
      .array(z.string().trim().min(1, 'Een regel is leeg.').max(2000, 'Een regel is te lang (maximaal 2000 tekens).'))
      .max(100, 'Maximaal 100 regels per blok.'),
  })
  .strict()

/** Per vakje de gekozen stand; alleen de vakjes die afwijken staan erin. */
const vinkjeOverrideSchema = z.record(z.string().min(1).max(100), z.boolean())

export const profielOverridesSchema = z
  .object({
    naam: z.string().trim().min(1, 'Vul een naam in').max(200).optional(),
    geboortejaar: z.string().trim().max(10).nullable().optional(),
    woonplaats: z.string().trim().max(200).nullable().optional(),
    /** Vervangt `klantProfielTekst` in dit document. */
    persoonlijkProfiel: z.string().max(6000).nullable().optional(),
    kerncompetenties: z.array(z.string().trim().min(1).max(300)).max(30).optional(),
    /** Vervangt de hele werkervaring: een blok weglaten = hem hier niet opnemen. */
    werkervaring: z.array(overrideWerkervaringSchema).max(30).optional(),
    functies: vinkjeOverrideSchema.optional(),
    beschikbaarheid: vinkjeOverrideSchema.optional(),
    fotoUitsnede: profielFotoUitsnedeSchema.optional(),
  })
  .strict()
export type ProfielOverrides = z.infer<typeof profielOverridesSchema>

// ─── Documentversies ─────────────────────────────────────────────────────────

export const PROFIEL_VERSIE_FORMATEN = ['pptx', 'pdf'] as const
export type ProfielVersieFormaat = (typeof PROFIEL_VERSIE_FORMATEN)[number]

export const PROFIEL_VERSIE_BRONNEN = ['EDITOR', 'UPLOAD'] as const
export type ProfielVersieBron = (typeof PROFIEL_VERSIE_BRONNEN)[number]

export const PROFIEL_VERSIE_STATUSSEN = ['ACTIEF', 'WACHT_OP_KEUZES'] as const
export type ProfielVersieStatus = (typeof PROFIEL_VERSIE_STATUSSEN)[number]

export const createProfielVersieSchema = z
  .object({
    /** Naam of doel, bv. "Aludyne, nov 2026". */
    naam: z.string().trim().min(1, 'Geef deze versie een naam of doel').max(160),
    overrides: profielOverridesSchema.optional(),
    gedeeldMetMedewerker: z.boolean().optional(),
  })
  .strict()
export type CreateProfielVersieInput = z.infer<typeof createProfielVersieSchema>

export const updateProfielVersieSchema = z
  .object({
    naam: z.string().trim().min(1, 'Geef deze versie een naam of doel').max(160).optional(),
    overrides: profielOverridesSchema.optional(),
    gedeeldMetMedewerker: z.boolean().optional(),
  })
  .strict()
export type UpdateProfielVersieInput = z.infer<typeof updateProfielVersieSchema>

export const genereerProfielVersieSchema = z
  .object({
    formaat: z.enum(PROFIEL_VERSIE_FORMATEN),
    /** Oude versies blijven op hun templateversie; dit is de bewuste keuze om te wisselen. */
    nieuweTemplate: z.boolean().optional(),
  })
  .strict()

export const genereerProfielSchema = z
  .object({
    formaat: z.enum(PROFIEL_VERSIE_FORMATEN),
    naam: z.string().trim().min(1).max(160).optional(),
  })
  .strict()

export interface ProfielVersieBestandDTO {
  documentId: string
  versionNr: number
  bestandsnaam: string
  gegenereerdOp: string
}

/**
 * Een waarschuwing uit de autofit, zoals opgeslagen bij de versie (§6.1). `soort`
 * bepaalt de kleur in de editor: TE_LANG en AFGEKAPT zijn rood, de rest amber.
 *
 * SCHATTING (batch-71 review): de passing is niet met de echte tekenbreedtes gerekend,
 * omdat een lettertype van de template geen maten heeft in `profiel-metrics` of omdat de
 * generator op de standaardtemplate draait. Geldt voor het hele document (`slide: null`).
 */
export const PROFIEL_WAARSCHUWING_SOORTEN = [
  'TE_LANG',
  'AFGEKAPT',
  'VERKLEIND',
  'COMPACT',
  'GEEN_FOTO',
  'LEEG_VELD',
  'SCHATTING',
] as const
export type ProfielWaarschuwingSoort = (typeof PROFIEL_WAARSCHUWING_SOORTEN)[number]

export interface ProfielWaarschuwing {
  soort: ProfielWaarschuwingSoort
  /** Canonieke token-key, of 'profiel-foto' / 'bedrijf' / 'layout' (SCHATTING). */
  token: string
  slide: number | null
  bericht: string
  regelsTeVeel?: number
  verborgen?: number
  puntgrootte?: number
}

/**
 * batch-71 (review: geschatte layout) - waarom de generator niet op de actieve template
 * draait maar op de standaardtemplate (de stand-in, of het bestand uit
 * `PROFIEL_TEMPLATE_PATH`). Dat stond alleen in de log; nu wordt de passing als schatting
 * gemeld (SCHATTING) in de editor, op de tab Profiel, in de versie en in de bulk-ZIP.
 */
export const PROFIEL_TEMPLATE_TERUGVAL_REDENEN = ['GEEN_ACTIEVE_TEMPLATE', 'NIET_TE_LADEN'] as const
export type ProfielTemplateTerugvalReden = (typeof PROFIEL_TEMPLATE_TERUGVAL_REDENEN)[number]

export interface ProfielTemplateTerugval {
  reden: ProfielTemplateTerugvalReden
  /** Het bestand dat in plaats daarvan gebruikt is (de standaardtemplate). */
  bestandsnaam: string
  /** Bij NIET_TE_LADEN: de versie van de actieve template die niet te laden was. */
  actieveVersie: number | null
}

export interface ProfielDocumentVersieDTO {
  id: string
  employeeId: string
  naam: string
  bron: ProfielVersieBron
  status: ProfielVersieStatus
  overrides: ProfielOverrides
  waarschuwingen: ProfielWaarschuwing[]
  gedeeldMetMedewerker: boolean
  /** Deze versie heeft een eigen foto (de foto van de medewerker blijft staan). */
  heeftEigenFoto: boolean
  templateId: string | null
  templateVersie: number | null
  /** Er is een nieuwere actieve template dan waar deze versie op staat. */
  nieuwereTemplateBeschikbaar: boolean
  pptx: ProfielVersieBestandDTO | null
  pdf: ProfielVersieBestandDTO | null
  gemaaktDoorNaam: string | null
  createdAt: string
  updatedAt: string
}

export interface ProfielVersieGenereerResultaat {
  versie: ProfielDocumentVersieDTO
  /** Bouwstenen voor de download via de same-origin proxy; nooit een navigeerbaar pad. */
  documentId: string
  versionNr: number
  bestandsnaam: string
  waarschuwingen: ProfielWaarschuwing[]
}

// ─── Terugzetten van een in PowerPoint bewerkt bestand ───────────────────────

export const PROFIEL_TERUGZET_VELDEN = [
  'naam',
  'geboortejaar',
  'woonplaats',
  'persoonlijkProfiel',
  'kerncompetenties',
  'werkervaring',
  'functies',
  'beschikbaarheid',
  'opleidingenVerplicht',
  'opleidingenAanvullend',
  'bedrijf',
] as const
export type ProfielTerugzetVeld = (typeof PROFIEL_TERUGZET_VELDEN)[number]

/** Overnemen op de medewerker / alleen deze versie / negeren (§3.B7, T9). */
export const PROFIEL_TERUGZET_KEUZES = ['OVERNEMEN', 'ALLEEN_VERSIE', 'NEGEREN'] as const
export type ProfielTerugzetKeuze = (typeof PROFIEL_TERUGZET_KEUZES)[number]

export const PROFIEL_TERUGZET_KEUZE_LABELS: Record<ProfielTerugzetKeuze, string> = {
  OVERNEMEN: 'Overnemen op de medewerker',
  ALLEEN_VERSIE: 'Alleen deze versie',
  NEGEREN: 'Negeren',
}

export interface ProfielTerugzetVerschil {
  veld: ProfielTerugzetVeld
  label: string
  /** Leesbare tekst van de huidige waarde op de medewerker. */
  huidig: string
  /** Leesbare tekst van de waarde in het bestand. */
  inBestand: string
  /** Welke keuzes hier kunnen; afgeleide velden kennen alleen NEGEREN. */
  keuzes: ProfielTerugzetKeuze[]
  /** Waarom niet alle keuzes kunnen (bv. "afgeleid van de certificaten"). */
  uitleg: string | null
  /**
   * `huidig` is de waarde in de versie waar het bestand uit kwam (die wijkt af van de
   * medewerker), niet die op de medewerker. Het scherm labelt de kolom daarnaar.
   */
  huidigUitVersie?: boolean
}

export interface ProfielTerugzetResultaat {
  versie: ProfielDocumentVersieDTO
  verschillen: ProfielTerugzetVerschil[]
}

export const profielTerugzetKeuzesSchema = z
  .object({
    keuzes: z
      .array(
        z
          .object({
            veld: z.enum(PROFIEL_TERUGZET_VELDEN),
            keuze: z.enum(PROFIEL_TERUGZET_KEUZES),
          })
          .strict(),
      )
      .max(PROFIEL_TERUGZET_VELDEN.length),
  })
  .strict()
export type ProfielTerugzetKeuzesInput = z.infer<typeof profielTerugzetKeuzesSchema>

// ─── Instelling (bevoegdheid + lettertypen) ──────────────────────────────────

export const profielInstellingSchema = z
  .object({
    /** A6: wie (naast MEDEWERKERS.canEdit) documentversies mag maken en bewerken. */
    versieBevoegdUserIds: z.array(z.string().min(1)).max(200).optional(),
    /** A3: lettertypen van de template ingesloten laten in de gegenereerde pptx. */
    fontsInsluiten: z.boolean().optional(),
  })
  .strict()
export type ProfielInstellingInput = z.infer<typeof profielInstellingSchema>

export interface ProfielLettertypeStatusDTO {
  /** Lettertypen die de template gebruikt. */
  nodig: string[]
  /** Daarvan niet beschikbaar op de server. */
  ontbrekend: string[]
  /** `false` = LibreOffice of fontconfig zelf ontbreekt op de server. */
  rendererBeschikbaar: boolean
}

export interface ProfielInstellingDTO {
  versieBevoegdUserIds: string[]
  versieBevoegdNamen: string[]
  fontsInsluiten: boolean
  lettertypeStatus: ProfielLettertypeStatusDTO
}

// ─── Weergave-DTO's ──────────────────────────────────────────────────────────

/** Verwijst de weergave naar de media van deze template. */
export interface ProfielWeergaveTemplateDTO {
  /** Template-id, of 'repo' als de generator op de repo-fallback draait. */
  ref: string
  versie: number | null
  bestandsnaam: string
}

export interface ProfielEditorDTO {
  layout: ProfielLayout
  template: ProfielWeergaveTemplateDTO
  /**
   * Draait de generator op de standaardtemplate (geen actieve of geen ladbare template),
   * dan staat hier waarom; null = de actieve template. De editor meldt de passing dan als
   * schatting, net als de generator.
   */
  templateTerugval: ProfielTemplateTerugval | null
  /** De medewerkerdata zonder document-overrides. */
  medewerker: ProfielData
  /** Handmatige beschikbaarheid op de medewerker (null = afgeleid). */
  beschikbaarheidOverride: ProfielBeschikbaarheidOverride | null
  fotoUitsnede: ProfielFotoUitsnede | null
  /** Werkervaring met id's, voor bewerken op de medewerker. */
  werkervaring: Array<{
    id: string
    functie: string
    bedrijf: string | null
    periodeVan: string | null
    periodeTot: string | null
    bullets: string[]
    volgorde: number
  }>
  versies: ProfielDocumentVersieDTO[]
  magBewerken: boolean
  magVersiesBeheren: boolean
  lettertypeStatus: ProfielLettertypeStatusDTO
  /**
   * De functies die met de hand aan de medewerker gekoppeld zijn (`Employee.profielFuncties`).
   * Een vinkje kan ook aan staan door de functietitel of een skill; alleen deze lijst is op de
   * medewerker te wijzigen (review I4).
   */
  handmatigeFuncties: ProfielFunctie[]
  /** Layouts van oudere templates waar een versie op vaststaat, op template-id (review K4). */
  versieLayouts: Record<string, ProfielLayout>
}

/**
 * Wat de klant te zien krijgt: uitsluitend klantveilige tokens (`klantwaardig` op
 * het token), alleen de slides over de persoon (1, 3 en 4). Geen tarieven-slide,
 * geen bedrijfsgegevens, geen vervaldatums, geen interne profieltekst.
 */
export interface KlantProfielWeergaveDTO {
  layout: ProfielLayout
  template: ProfielWeergaveTemplateDTO
  data: ProfielData
  heeftFoto: boolean
  fotoUitsnede: ProfielFotoUitsnede | null
}

/** De slides die de klant ziet. */

/**
 * batch-71 (FO-538, review K1) - de hele werkervaring in één keer op de medewerker: wat nieuw
 * is erbij, wat ontbreekt eraf (soft delete), de volgorde op de lijstpositie. Eén transactie,
 * dus nooit een half bijgewerkt dossier.
 */
export const werkervaringBlokSchema = z.object({
  functie: z
    .string()
    .trim()
    .min(1, 'Elk blok heeft een functie nodig.')
    .max(500, 'De functie is te lang (maximaal 500 tekens).'),
  bedrijf: z.string().trim().max(500, 'De naam van het bedrijf is te lang (maximaal 500 tekens).').nullable(),
  periodeVan: z.string().trim().max(100, 'De periode is te lang (maximaal 100 tekens).').nullable(),
  periodeTot: z.string().trim().max(100, 'De periode is te lang (maximaal 100 tekens).').nullable(),
  bullets: z
    .array(z.string().trim().min(1, 'Een regel is leeg.').max(2000, 'Een regel is te lang (maximaal 2000 tekens).'))
    .max(100, 'Maximaal 100 regels per blok.'),
})

/** Eén blok via de losse CRUD (POST): zelfde grenzen als de lijst (review N4). */
export const werkervaringCreateSchema = werkervaringBlokSchema
  .partial({ bedrijf: true, periodeVan: true, periodeTot: true, bullets: true })
  .extend({ volgorde: z.number().int().optional() })
export const werkervaringUpdateSchema = werkervaringCreateSchema.partial()

export const werkervaringLijstSchema = z.object({
  items: z
    .array(werkervaringBlokSchema.extend({ id: z.string().min(1).nullable() }))
    .max(100, 'Maximaal 100 blokken werkervaring.')
    .refine(
      (items) => {
        const ids = items.map((i) => i.id).filter((id): id is string => id !== null)
        return new Set(ids).size === ids.length
      },
      { message: 'Een blok staat twee keer in de lijst.' },
    ),
})
export type WerkervaringLijstInput = z.infer<typeof werkervaringLijstSchema>
